import React, { lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import AuthGate from '../components/AuthGate';
import PublicAnalyticsRouteTracker from '../components/PublicAnalyticsRouteTracker';
import RouteLoadBoundary, { RouteLoadingFallback } from '../components/RouteLoadBoundary';

// Route-family boundaries keep public visitors from downloading authenticated,
// participant, admin, setup, Scenario, and export implementations up front.
const Login = lazy(() => import(/* webpackChunkName: "route-auth" */ './Login'));
const AuthPage = lazy(() => import(/* webpackChunkName: "route-auth" */ './AuthPage'));
const StartPage = lazy(() => import(/* webpackChunkName: "route-start" */ './Start/StartPage'));
const OutreachLandingPage = lazy(
  () => import(/* webpackChunkName: "route-outreach" */ './OutreachLandingPage'),
);
const BeforeEveryoneAgreesPage = lazy(
  () => import(/* webpackChunkName: "route-outreach" */ './BeforeEveryoneAgreesPage'),
);

const About = lazy(() => import(/* webpackChunkName: "route-public" */ './About'));
const PrivacyPage = lazy(() => import(/* webpackChunkName: "route-public" */ './PrivacyPage'));
const TermsPage = lazy(() => import(/* webpackChunkName: "route-public" */ './TermsPage'));
const DpaPage = lazy(() => import(/* webpackChunkName: "route-public" */ './DpaPage'));
const CookieNoticePage = lazy(
  () => import(/* webpackChunkName: "route-public" */ './CookieNoticePage'),
);
const SubprocessorsPage = lazy(
  () => import(/* webpackChunkName: "route-public" */ './SubprocessorsPage'),
);
const ProductPage = lazy(() => import(/* webpackChunkName: "route-public" */ './ProductPage'));
const PricingPage = lazy(() => import(/* webpackChunkName: "route-public" */ './PricingPage'));
const BoardMeetingPreparationPage = lazy(
  () => import(/* webpackChunkName: "route-public" */ './BoardMeetingPreparationPage'),
);
const WhyScenariosPage = lazy(
  () => import(/* webpackChunkName: "route-public" */ './WhyScenariosPage'),
);
const SecurityPage = lazy(() => import(/* webpackChunkName: "route-public" */ './SecurityPage'));
const NotFoundPage = lazy(() => import(/* webpackChunkName: "route-public" */ './NotFoundPage'));

const GuestEmailPreferencesPage = lazy(
  () => import(/* webpackChunkName: "route-participant" */ './GuestEmailPreferencesPage'),
);
const ParticipantRoutePage = lazy(
  () => import(/* webpackChunkName: "route-participant" */ './ParticipantRoutePage'),
);
const FastScenarioParticipantPage = lazy(
  () => import(/* webpackChunkName: "route-participant" */ './FastScenarioParticipantPage'),
);
const ScenarioFastTokenParticipantPage = lazy(
  () =>
    import(
      /* webpackChunkName: "route-participant" */ './ScenarioFast/ScenarioFastParticipantPage'
    ),
);
const ParticipantInvitationPage = lazy(
  () => import(/* webpackChunkName: "route-participant" */ './ParticipantInvitationPage'),
);

const DashboardPage = lazy(
  () => import(/* webpackChunkName: "route-organizer" */ './DashboardPage'),
);
const ChatHistoryPage = lazy(
  () => import(/* webpackChunkName: "route-organizer" */ './ChatHistoryPage'),
);
const SetupPage = lazy(() => import(/* webpackChunkName: "route-setup" */ './SetupPage'));

const ScenarioFastOrganizerPage = lazy(
  () => import(/* webpackChunkName: "route-scenario" */ './ScenarioFast/ScenarioFastOrganizerPage'),
);
const ScenarioMapPage = lazy(
  () => import(/* webpackChunkName: "route-scenario" */ './ScenarioFast/ScenarioMapPage'),
);
const FastScenarioCreateFlow = lazy(
  () => import(/* webpackChunkName: "route-scenario" */ './ScenarioFast/FastScenarioCreateFlow'),
);
const FastScenarioContinueFlow = lazy(
  () => import(/* webpackChunkName: "route-scenario" */ './ScenarioFast/FastScenarioContinueFlow'),
);

const AdminPanel = lazy(() => import(/* webpackChunkName: "route-admin" */ './AdminPanel'));
const TrainingStats = lazy(() => import(/* webpackChunkName: "route-admin" */ './TrainingStats'));
const GlobalPromptConfigurator = lazy(
  () => import(/* webpackChunkName: "route-admin" */ './GlobalPromptConfigurator'),
);
const AdminAccessGate = lazy(
  () => import(/* webpackChunkName: "route-admin" */ '../components/AdminAccessGate'),
);

const SummaryProposalPage = lazy(
  () => import(/* webpackChunkName: "route-summary-export" */ './SummaryProposalPage'),
);
const BoardOutcomeSharePage = lazy(
  () => import(/* webpackChunkName: "route-board-share" */ './BoardOutcomeSharePage'),
);

function ScenarioCreateRedirect() {
  const { companyId = '' } = useParams();
  return <Navigate to={`/instance/${encodeURIComponent(companyId)}/scenario/new`} replace />;
}

export function shouldOpenLegacySetupRoute(searchParams) {
  return Boolean(String(searchParams?.get?.('projectId') || '').trim());
}

function ScenarioOnlySetupRoute() {
  const { companyId = '' } = useParams();
  const [searchParams] = useSearchParams();
  if (shouldOpenLegacySetupRoute(searchParams)) {
    return <SetupPage />;
  }

  return <Navigate to={`/instance/${encodeURIComponent(companyId)}/scenario/new`} replace />;
}

const TeamMemoryPage = lazy(() => import('../features/teamMemory/TeamMemoryPage'));
const OAuthConsentPage = lazy(() => import('./OAuthConsentPage'));
const MemoryJoinPage = lazy(() => import('../features/teamMemory/MemoryJoinPage'));

function InstanceRoutes() {
  return (
    <Routes>
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route path="setup" element={<ScenarioOnlySetupRoute />} />
      <Route path="dashboard" element={process.env.REACT_APP_TEAM_MEMORY_ENABLED === 'true' ? <TeamMemoryPage entry /> : <DashboardPage />} />
      <Route path="workflows" element={<TeamMemoryPage />} />
      <Route path="workflows/:workflowId" element={<TeamMemoryPage />} />
      <Route path="settings" element={<TeamMemoryPage settings />} />
      <Route path="chat-history/:projectId" element={<ChatHistoryPage />} />
      <Route path="summary/:projectId" element={<SummaryProposalPage />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <PublicAnalyticsRouteTracker />
      <RouteLoadBoundary>
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<StartPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/join" element={<MemoryJoinPage />} />
            <Route path="/oauth/consent" element={<OAuthConsentPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/about" element={<About />} />
            <Route path="/start" element={<StartPage />} />
            <Route path="/product" element={<ProductPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route
              path="/solutions/board-meeting-preparation"
              element={<BoardMeetingPreparationPage />}
            />
            <Route path="/why-scenarios" element={<WhyScenariosPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/dpa" element={<DpaPage />} />
            <Route path="/cookies" element={<CookieNoticePage />} />
            <Route path="/subprocessors" element={<SubprocessorsPage />} />
            <Route
              path="/try/team-intelligence"
              element={<OutreachLandingPage variantId="team_intelligence_v1" />}
            />
            <Route
              path="/try/strategy-rhythm"
              element={<OutreachLandingPage variantId="strategy_rhythm_v1" />}
            />
            <Route path="/try/before-everyone-agrees" element={<BeforeEveryoneAgreesPage />} />
            <Route path="/board-outcome/:token" element={<BoardOutcomeSharePage />} />
            <Route path="/participate" element={<ParticipantInvitationPage />} />
            <Route path="/email-preferences/guest" element={<GuestEmailPreferencesPage />} />

            {/* Team Memory checks the requested membership through its canonical API;
                it must not inherit the legacy single-company profile redirect. */}
            <Route path="/instance/:companyId/workflows" element={<TeamMemoryPage />} />
            <Route path="/instance/:companyId/workflows/:workflowId" element={<TeamMemoryPage />} />
            <Route path="/instance/:companyId/settings" element={<TeamMemoryPage settings />} />

            {/* Explicit team member route */}
            <Route
              path="/instance/:companyId/team/:projectId/:teamMemberId"
              element={<ParticipantRoutePage />}
            />
            <Route
              path="/instance/:companyId/scenario-fast/:projectId/team/:teamMemberId"
              element={<FastScenarioParticipantPage />}
            />
            <Route
              path="/instance/:companyId/scenario-fast/:projectId/:teamMemberId"
              element={<FastScenarioParticipantPage />}
            />
            <Route
              path="/participant/scenario-fast/:token"
              element={<ScenarioFastTokenParticipantPage />}
            />
            <Route
              path="/instance/:companyId/scenario-fast/:projectId/map"
              element={
                <AuthGate>
                  <ScenarioMapPage />
                </AuthGate>
              }
            />
            <Route
              path="/instance/:companyId/scenario-fast/:projectId/versions/:versionId"
              element={
                <AuthGate>
                  <ScenarioMapPage />
                </AuthGate>
              }
            />
            <Route
              path="/instance/:companyId/scenario-fast/:projectId/versions/:versionId/invite"
              element={
                <AuthGate>
                  <ScenarioMapPage />
                </AuthGate>
              }
            />
            <Route
              path="/instance/:companyId/scenario-fast/:projectId/continue"
              element={
                <AuthGate>
                  <FastScenarioContinueFlow />
                </AuthGate>
              }
            />
            <Route
              path="/instance/:companyId/project/:projectId/scenario-map"
              element={
                <AuthGate>
                  <ScenarioMapPage />
                </AuthGate>
              }
            />
            <Route
              path="/instance/:companyId/scenario-fast/:projectId"
              element={
                <AuthGate>
                  <ScenarioFastOrganizerPage />
                </AuthGate>
              }
            />
            <Route
              path="/instance/:companyId/scenario/new"
              element={
                <AuthGate>
                  <FastScenarioCreateFlow />
                </AuthGate>
              }
            />
            <Route
              path="/instance/:companyId/board-prep/new/fast-scenario"
              element={
                <AuthGate>
                  <ScenarioCreateRedirect />
                </AuthGate>
              }
            />
            <Route
              path="/instance/:companyId/summary/:projectId"
              element={
                <AuthGate>
                  <SummaryProposalPage />
                </AuthGate>
              }
            />

            {/* Instance‑specific routes */}
            <Route
              path="/instance/:companyId/*"
              element={
                <AuthGate>
                  <InstanceRoutes />
                </AuthGate>
              }
            />

            {/* Global admin routes */}
            <Route
              path="/admin/training-stats"
              element={
                <AdminAccessGate>
                  <TrainingStats />
                </AdminAccessGate>
              }
            />
            <Route
              path="/admin/global-config"
              element={
                <AdminAccessGate>
                  <GlobalPromptConfigurator />
                </AdminAccessGate>
              }
            />
            <Route
              path="/admin"
              element={
                <AdminAccessGate>
                  <AdminPanel />
                </AdminAccessGate>
              }
            />

            {/* Global fallback */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </RouteLoadBoundary>
    </Router>
  );
}
